import React, { useEffect, useRef, useState } from "react";
import { stats } from "../mock";
import { Users, Handshake, FileCheck2, Gavel } from "lucide-react";

const iconMap = { 1: Users, 2: Handshake, 3: FileCheck2, 4: Gavel };

function useInView(ref) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && setInView(true),
      { threshold: 0.3 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return inView;
}

function Counter({ value, active }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!active) return;
    const dur = 1500;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.floor(value * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
      else setN(value);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, value]);
  return <span>{n.toLocaleString()}</span>;
}

export default function StatsSection() {
  const ref = useRef(null);
  const inView = useInView(ref);

  return (
    <section
      ref={ref}
      className="relative py-20 bg-fixed bg-center bg-cover"
      style={{
        backgroundImage:
          "linear-gradient(rgba(10, 20, 40, 0.9), rgba(10, 20, 40, 0.92)), url('https://images.pexels.com/photos/5668882/pexels-photo-5668882.jpeg?auto=compress&cs=tinysrgb&w=1920')",
      }}
    >
      <div className="container-x">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-[#c9a961] uppercase tracking-[0.35em] text-xs font-semibold">Why Choose Us</span>
          <h2 className="font-serif-display text-white text-3xl md:text-4xl mt-3 gold-underline center">
            We have great results
          </h2>
          <p className="text-neutral-300 mt-6">
            There is a big difference between a satisfied client and a loyal client. We never settle for &lsquo;satisfied&rsquo;.
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((s) => {
            const Icon = iconMap[s.id];
            return (
              <div
                key={s.id}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-sm p-8 text-center hover:border-[#c9a961]/60 hover:bg-white/10 transition-colors"
              >
                <div className="w-14 h-14 rounded-sm mx-auto bg-[#c9a961]/15 border border-[#c9a961]/40 flex items-center justify-center mb-5">
                  <Icon className="text-[#c9a961]" size={22} />
                </div>
                <div className="font-serif-display text-4xl md:text-5xl text-white font-bold">
                  <Counter value={s.value} active={inView} />
                </div>
                <div className="text-[#c9a961] text-xs uppercase tracking-[0.25em] mt-2 font-semibold">
                  {s.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
